"use client";
import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";
import { useAuth } from "../context/AuthContext";

const HolidayContext = createContext();

export const useHolidays = () => useContext(HolidayContext);

export const HolidayProvider = ({ children }) => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { isAuthenticated, token, user } = useAuth();

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // Fetch Holidays
  const fetchHolidays = async () => {
    if (!isAuthenticated || !token) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${BASE_URL}/holidays`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!res.ok) {
        setError("Failed to fetch holidays");
        return {
          success: false,
          message: "Failed to fetch holidays",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        setHolidays(data.data || []);

        return {
          success: true,
          data: data.data,
        };
      }

      setError(data.message || "Failed to fetch holidays");

      return {
        success: false,
        message: data.message || "Failed to fetch holidays",
      };
    } catch (err) {
      console.error("Error fetching holidays:", err);

      setError("Network error. Please try again.");

      return {
        success: false,
        message: "Network error. Please try again.",
      };
    } finally {
      setLoading(false);
    }
  };

  // Create Holiday
  const addHoliday = async (holiday) => {
    try {
      const create_audit_id = user?.id || null;

      const res = await fetch(`${BASE_URL}/holidays`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...holiday, create_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to add holiday",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 201) {
        await fetchHolidays();

        toast.success(data.message || "Holiday added successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to add holiday",
      };
    } catch (err) {
      console.error("Error adding holiday:", err);

      return {
        success: false,
        message: "Network error. Please try again.",
      };
    }
  };

  // Update Holiday
  const updateHoliday = async (id, holiday) => {
    try {
      const update_audit_id = user?.id || null;

      const res = await fetch(`${BASE_URL}/holidays/${id}`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...holiday, update_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update holiday",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        setHolidays((prev) =>
          prev.map((h) => (h.id === id ? { ...h, ...holiday } : h))
        );

        toast.success(data.message || "Holiday updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update holiday",
      };
    } catch (err) {
      console.error("Error updating holiday:", err);

      return {
        success: false,
        message: "Network error. Please try again.",
      };
    }
  };

  // Toggle Status
  const toggleStatusHoliday = async (id, currentStatus) => {
    try {
      const update_audit_id = user?.id || null;
      const newStatus = currentStatus === "active" ? "inactive" : "active";

      const res = await fetch(`${BASE_URL}/holidays/${id}/status`, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: newStatus,
          update_audit_id,
        }),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to update status",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        setHolidays((prev) =>
          prev.map((h) => (h.id === id ? { ...h, status: newStatus } : h))
        );

        toast.success(data.message || "Holiday status updated successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update status",
      };
    } catch (err) {
      console.error("Error toggling holiday status:", err);

      return {
        success: false,
        message: "Network error. Please try again.",
      };
    }
  };

  // Delete Holiday
  const deleteHoliday = async (id) => {
    try {
      const delete_audit_id = user?.id || null;

      const res = await fetch(`${BASE_URL}/holidays/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ delete_audit_id }),
      });

      if (!res.ok) {
        const data = await parseJson(res);

        return {
          success: false,
          message: data?.message || "Failed to delete holiday",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        setHolidays((prev) => prev.filter((holiday) => holiday.id !== id));

        toast.success(data.message || "Holiday deleted successfully");

        return {
          success: true,
          message: data.message,
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to delete holiday",
      };
    } catch (err) {
      console.error("Error deleting holiday:", err);

      return {
        success: false,
        message: "Network error. Please try again.",
      };
    }
  };

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchHolidays();
    }
  }, [isAuthenticated, token]);

  return (
    <HolidayContext.Provider
      value={{
        holidays,
        loading,
        error,
        fetchHolidays,
        addHoliday,
        updateHoliday,
        deleteHoliday,
        toggleStatusHoliday,
      }}
    >
      {children}
    </HolidayContext.Provider>
  );
};